export const ThermostatConstants = {
  ClearRefreshedPin: 'THERMOSTAT_CONSTANTS.CLEAR_REFRESHED_PIN',
  RestoreDefaults: 'THERMOSTAT_CONSTANTS.RESTORE_DEFAULTS',
  SetRestoreMessage: 'THERMOSTAT_CONSTANTS.SET_RESTORE_MESSAGE',
  ClearRestoreResults: 'THERMOSTAT_CONSTANTS.CLEAR_RESTORE_RESULTS',
  ClearThermostatError: 'THERMOSTAT_CONSTANTS.CLEAR_THERMOSTAT_ERROR',
  RequestNewPin: 'THERMOSTAT_CONSTANTS.REQUEST_NEW_PIN',
  ReceiveNewPin: 'THERMOSTAT_CONSTANTS.RECEIVE_NEW_PIN',
  RequestNewThermostat: 'THERMOSTAT_CONSTANTS.REQUEST_NEW_THERMOSTAT',
  ReceiveNewThermostat: 'THERMOSTAT_CONSTANTS.RECEIVE_NEW_THERMOSTAT',
  RequestThermostats: 'THERMOSTAT_CONSTANTS.REQUEST_THERMOSTATS',
  ReceiveThermostats: 'THERMOSTAT_CONSTANTS.RECEIVE_THERMOSTATS',
  StopRegistering: 'THERMOSTAT_CONSTANTS.STOP_REGISTERING',
  ThermostatError: 'THERMOSTAT_CONSTANTS.THERMOSTAT_ERROR',
  UpdateFilterCounts: 'THERMOSTAT_CONSTANTS.UPDATE_FILTER_COUNTS',
  UpdateFilteredThermostats: 'THERMOSTAT_CONSTANTS.UPDATE_FILTERED_THERMOSTATS',
  UpdateFilters: 'THERMOSTAT_CONSTANTS.UPDATE_FILTERS',
  UpdateNumberOfFilters: 'THERMOSTAT_CONSTANTS.UPDATE_NUMBER_OF_FILTERS',
  PollThermostat: 'THERMOSTAT_CONSTANTS.POLL_THERMOSTAT',
};

// ACTION Types
interface ClearRefreshedPinAction {
  type: typeof ThermostatConstants.ClearRefreshedPin;
  payload: undefined;
}

interface RestoreDefaultsAction {
  type: typeof ThermostatConstants.RestoreDefaults;
  payload: undefined;
}

interface SetRestoreMessageAction {
  type: typeof ThermostatConstants.SetRestoreMessage;
  payload: string;
}

interface ClearRestoreResultsAction {
  type: typeof ThermostatConstants.ClearRestoreResults;
  payload: undefined;
}

interface ClearThermostatErrorAction {
  type: typeof ThermostatConstants.ClearThermostatError;
  payload: undefined;
}

interface RequestNewPinAction {
  type: typeof ThermostatConstants.RequestNewPin;
  payload: undefined;
}

interface ReceiveNewPinAction {
  type: typeof ThermostatConstants.ReceiveNewPin;
  payload: string;
}

interface RequestNewThermostatAction {
  type: typeof ThermostatConstants.RequestNewThermostat;
  payload: undefined;
}

interface ReceiveNewThermostatAction {
  type: typeof ThermostatConstants.ReceiveNewThermostat;
  payload: Thermostat;
}

interface RequestThermostatsAction {
  type: typeof ThermostatConstants.RequestThermostats;
  payload: undefined;
}

interface ReceiveThermostatsAction {
  type: typeof ThermostatConstants.ReceiveThermostats;
  payload: Array<Thermostat>;
}

interface StopRegisteringAction {
  type: typeof ThermostatConstants.StopRegistering;
  payload: undefined;
}

interface ThermostatErrorAction {
  type: typeof ThermostatConstants.ThermostatError;
  payload: string;
}

interface UpdateFilterCountsAction {
  type: typeof ThermostatConstants.UpdateFilterCounts;
  payload: ThermostatFilterCount;
}

interface UpdateFilteredThermostatsAction {
  type: typeof ThermostatConstants.UpdateFilteredThermostats;
  payload: Array<Thermostat>;
}

interface UpdateFiltersAction {
  type: typeof ThermostatConstants.UpdateFilters;
  payload: ThermostatFilters;
}

interface UpdateNumberOfFiltersAction {
  type: typeof ThermostatConstants.UpdateNumberOfFilters;
  payload: number;
}

interface PollThermostatAction {
  type: typeof ThermostatConstants.PollThermostat;
  payload: PollResult;
}

export type ThermostatActionTypes =
  | ClearRefreshedPinAction
  | ClearRestoreResultsAction
  | RestoreDefaultsAction
  | SetRestoreMessageAction
  | ClearThermostatErrorAction
  | RequestNewPinAction
  | ReceiveNewPinAction
  | RequestNewThermostatAction
  | ReceiveNewThermostatAction
  | RequestThermostatsAction
  | ReceiveThermostatsAction
  | StopRegisteringAction
  | ThermostatErrorAction
  | UpdateFilterCountsAction
  | UpdateFilteredThermostatsAction
  | UpdateFiltersAction
  | UpdateNumberOfFiltersAction
  | PollThermostatAction;

// STATE slices
export interface Thermostat {
  pin: string;
  uid_hash: string;
  direction: string;
  unit_number: string;
  display_name: string;
  floor_number: string;
  building_slug: string;
  thermostat_type: string;
  building_section: string;
}

export interface CurrentThermostat {
  mode: string;
  state: string;
  uid_hash: string;
  fan_mode: string;
  direction: string;
  is_occupied: boolean;
  unit_number: string;
  display_name: string;
  floor_number: number;
  serial_number: string;
  building_slug: string;
  is_fan_running: boolean;
  thermostat_type: string;
  building_section: string;
  temperature_unit: string;
  connection_state: string;
  target_temperature: string;
  ambient_temperature: string;
  connection_timestamp: string;
  target_temperature_low: string;
  target_temperature_high: string;
  disconnection_timestamp: string;
  status_update_timestamp: string;
  temperature_satisfaction: string;
}

interface ConnectionStateFilters {
  offline?: boolean;
  online?: boolean;
  reset?: boolean;
}

interface DemandFilters {
  is_fan_running?: boolean;
  is_occupied?: boolean;
}

interface DirectionFilters {
  north?: boolean;
  north_east?: boolean;
  north_west?: boolean;
  west?: boolean;
  east?: boolean;
  south_east?: boolean;
  south_west?: boolean;
  south?: boolean;
}

interface FanModeFilters {
  auot?: boolean;
  on?: boolean;
  off?: boolean;
}

interface BuildingSectionFilters {
  top?: boolean;
  middle?: boolean;
  bottom?: boolean;
}

interface ModeFilters {
  cool?: boolean;
  eco?: boolean;
  heat?: boolean;
  off?: boolean;
  auto?: boolean;
  auxHeatOnly?: boolean;
  heat_cool?: boolean;
}

interface StateFilters {
  heat?: boolean;
  cool?: boolean;
  aux_heat?: boolean;
  off?: boolean;
}

export interface ThermostatFilters {
  connection_state: ConnectionStateFilters;
  demand: DemandFilters;
  direction: DirectionFilters;
  fan_mode: FanModeFilters;
  building_section: BuildingSectionFilters;
  mode: ModeFilters;
  searchValue: string;
  state: StateFilters;
}

export interface PollResult {
  success: boolean;
  should_poll: boolean;
}

interface ModeCount {
  auto: number;
  auxHeatOnly: number;
  cool: number;
  eco: number;
  heat: number;
  heat_cool: number;
  off: number;
}

interface FanModeCount {
  auto: number;
  off: number;
  on: number;
}

interface ConnectionStateCount {
  offline: number;
  online: number;
  reset: number;
}

interface StateCount {
  aux_heat: number;
  cool: number;
  heat: number;
  off: number;
}

interface DemandCount {
  is_fan_running: number;
  is_occupied: number;
}

export interface ThermostatFilterCount {
  mode: ModeCount;
  fan_mode: FanModeCount;
  connection_state: ConnectionStateCount;
  state: StateCount;
  demand: DemandCount;
}

export interface ThermostatReducer {
  newRegisteredThermostat?: Thermostat;
  refreshedPin?: string;
  allThermostats: Thermostat[];
  allThermostatsCount: number;
  error?: string;
  isFetching: boolean;
  refreshingPin: boolean;
  isRegistering: boolean;
  isRestoring: boolean;
  restoreMessage?: string;
  thermostatFilters: ThermostatFilters;
  numberOfFilters: number;
  poll: PollResult;
  filteredThermostats: Thermostat[];
  filteredThermostatCount: number;
  thermostatFilterCounts: ThermostatFilterCount;
}
